import React, { useState, useCallback } from 'react';
import type { Editor } from '@tiptap/core';
import type { ToolbarConfig, EditorCallbacks } from '../types';
import { ColorPicker } from './ColorPicker';
import { LinkDialog } from './LinkDialog';
import { ImageDialog } from './ImageDialog';
import { TableMenu } from './TableMenu';
import { FontSizePicker } from './FontSizePicker';
import { FontFamilyPicker } from './FontFamilyPicker';
import type { FontFamilyOption } from './FontFamilyPicker';

interface ToolbarProps {
  editor: Editor;
  config?: ToolbarConfig;
  callbacks?: EditorCallbacks;
  colorPalette?: string[];
  highlightPalette?: string[];
  fontSizes?: string[];
  fontFamilies?: FontFamilyOption[];
}

const defaultConfig: ToolbarConfig = {
  bold: true,
  italic: true,
  underline: true,
  strike: true,
  subscript: false,
  superscript: false,
  fontSize: true,
  fontFamily: true,
  textColor: true,
  highlight: true,
  headings: true,
  textAlign: true,
  bulletList: true,
  orderedList: true,
  taskList: true,
  blockquote: true,
  codeBlock: true,
  horizontalRule: true,
  link: true,
  image: true,
  table: true,
};

export const Toolbar: React.FC<ToolbarProps> = ({
  editor,
  config = defaultConfig,
  callbacks,
  colorPalette,
  highlightPalette,
  fontSizes,
  fontFamilies,
}) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);

  const mergedConfig = { ...defaultConfig, ...config };

  const handleLinkClick = useCallback(async () => {
    if (callbacks?.onLinkInsert) {
      const currentUrl = editor.getAttributes('link').href;
      const result = await callbacks.onLinkInsert(currentUrl);
      if (result) {
        editor.chain().focus().setLink(result).run();
      }
    } else {
      setShowLinkDialog(true);
    }
  }, [editor, callbacks]);

  const handleImageClick = useCallback(async () => {
    if (callbacks?.onImageInsert) {
      const result = await callbacks.onImageInsert();
      if (result) {
        editor.chain().focus().setImage(result).run();
      }
    } else {
      setShowImageDialog(true);
    }
  }, [editor, callbacks]);

  return (
    <div className="rich-editor-toolbar">
      {/* Text Formatting Group */}
      <div className="toolbar-group">
        {mergedConfig.bold && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
        )}

        {mergedConfig.italic && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
        )}

        {mergedConfig.underline && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'is-active' : ''}
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </button>
        )}

        {mergedConfig.strike && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            className={editor.isActive('strike') ? 'is-active' : ''}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
        )}

        {mergedConfig.subscript && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            className={editor.isActive('subscript') ? 'is-active' : ''}
            title="Subscript"
          >
            X<sub>2</sub>
          </button>
        )}

        {mergedConfig.superscript && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            className={editor.isActive('superscript') ? 'is-active' : ''}
            title="Superscript"
          >
            X<sup>2</sup>
          </button>
        )}
        {mergedConfig.fontFamily && (
          <FontFamilyPicker editor={editor} fontFamilies={fontFamilies} />
        )}
        {mergedConfig.fontSize && (
          <FontSizePicker editor={editor} fontSizes={fontSizes} />
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Color Group */}
      {(mergedConfig.textColor || mergedConfig.highlight) && (
        <>
          <div className="toolbar-group">
            {mergedConfig.textColor && (
              <ColorPicker editor={editor} type="color" palette={colorPalette} />
            )}
            {mergedConfig.highlight && (
              <ColorPicker editor={editor} type="highlight" palette={highlightPalette} />
            )}
          </div>
          <div className="toolbar-divider" />
        </>
      )}

      {/* Headings Group */}
      {mergedConfig.headings && (
        <>
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
              title="Heading 1"
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
              title="Heading 2"
            >
              H2
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
              title="Heading 3"
            >
              H3
            </button>
          </div>
          <div className="toolbar-divider" />
        </>
      )}

      {/* Alignment Group */}
      {mergedConfig.textAlign && (
        <>
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
              title="Align Left"
            >
              &#8676;
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
              title="Align Center"
            >
              &#8596;
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
              title="Align Right"
            >
              &#8677;
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
              className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
              title="Justify"
            >
              &#8801;
            </button>
          </div>
          <div className="toolbar-divider" />
        </>
      )}

      {/* Lists Group */}
      <div className="toolbar-group">
        {mergedConfig.bulletList && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            title="Bullet List"
          >
            &#8226; List
          </button>
        )}

        {mergedConfig.orderedList && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            title="Numbered List"
          >
            1. List
          </button>
        )}

        {mergedConfig.taskList && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            className={editor.isActive('taskList') ? 'is-active' : ''}
            title="Task List"
          >
            &#9744;
          </button>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Block Elements Group */}
      <div className="toolbar-group">
        {mergedConfig.blockquote && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive('blockquote') ? 'is-active' : ''}
            title="Blockquote"
          >
            &ldquo;
          </button>
        )}

        {mergedConfig.codeBlock && (
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={editor.isActive('codeBlock') ? 'is-active' : ''}
            title="Code Block"
          >
            &lt;/&gt;
          </button>
        )}

        {mergedConfig.horizontalRule && (
          <button
            type="button"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Horizontal Rule"
          >
            &#8213;
          </button>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Interactive Elements Group */}
      <div className="toolbar-group">
        {mergedConfig.link && (
          <button
            type="button"
            onClick={handleLinkClick}
            className={editor.isActive('link') ? 'is-active' : ''}
            title="Insert Link"
          >
            Link
          </button>
        )}

        {mergedConfig.image && (
          <button
            type="button"
            onClick={handleImageClick}
            title="Insert Image"
          >
            Image
          </button>
        )}
      </div>

      {/* Table Group */}
      {mergedConfig.table && (
        <>
          <div className="toolbar-divider" />
          <div className="toolbar-group">
            <TableMenu editor={editor} />
          </div>
        </>
      )}

      {/* Dialogs */}
      {showLinkDialog && (
        <LinkDialog editor={editor} onClose={() => setShowLinkDialog(false)} />
      )}
      {showImageDialog && (
        <ImageDialog editor={editor} onClose={() => setShowImageDialog(false)} />
      )}
    </div>
  );
};

export default Toolbar;
